import { createContext, useContext } from 'react';
import { RawFieldData } from '@shared/scenes/entity/types';

export interface CaptureSessionValue {
  /**
   * The gateway's id for this form's Capture conversation, or undefined before the first run
   * finishes. Read at send time rather than at mount, because the first run is what mints it.
   */
  getThreadId: () => string | undefined;
  /** Takes the id the gateway issued, so the next Capture on this form continues the same thread. */
  setThreadId: (threadId: string) => void;
  /**
   * What the form holds right now, keyed by component id, or undefined when nothing is providing it.
   *
   * Sent back on a refinement turn so the agent is proposing against the form the user is actually
   * looking at. The gateway remembers what *it* proposed, but not what happened next — the user may
   * have corrected a field, cleared one, or filled one in by hand between the two turns, and without
   * this the second turn would argue with edits it cannot see.
   */
  getCurrentFields: () => RawFieldData | undefined;
}

/**
 * One Capture conversation per open create form.
 *
 * Absent rather than defaulted, so a Capture without a form above it — which cannot happen through
 * the router, but can in a test — is a single-turn Capture rather than one silently sharing a thread
 * with nothing. `useCaptureSession` returns undefined and `useCapture` treats every run as a first.
 */
export const CaptureSessionContext = createContext<CaptureSessionValue | undefined>(undefined);

export function useCaptureSession(): CaptureSessionValue | undefined {
  return useContext(CaptureSessionContext);
}
